import { useState, useEffect } from "react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Select } from "../ui/Select";
import { useToast } from "../ui/ToastContainer";
import { enrollmentService } from "../../services/enrollmentService";
import { parseApiError } from "../../utils/errorHandler";

interface EditEnrollmentStatusDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  enrollment: any; // Using any for now to avoid strict type issues, but should match Enrollment type
}

export function EditEnrollmentStatusDialog({
  isOpen,
  onClose,
  onSuccess,
  enrollment,
}: EditEnrollmentStatusDialogProps) {
  const [status, setStatus] = useState("active");
  const [enrollmentExpiresAt, setEnrollmentExpiresAt] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    if (enrollment) {
      if (enrollment.status) {
        setStatus(enrollment.status || "active");
      } else {
        setStatus("active");
      }

      if (enrollment.enrollmentExpiresAt) {
        setEnrollmentExpiresAt(
          new Date(enrollment.enrollmentExpiresAt)
            .toISOString()
            .split("T")[0] || ""
        );
      } else {
        setEnrollmentExpiresAt("");
      }
      setError(null);
    }
  }, [enrollment]);

  const handleSave = async () => {
    if (!enrollment) return;

    setLoading(true);
    setError(null);
    try {
      await enrollmentService.updateEnrollment(enrollment._id, {
        status,
        enrollmentExpiresAt: enrollmentExpiresAt || null,
      });
      showSuccess(`Enrollment updated successfully`, "Updated");
      onSuccess();
      onClose();
    } catch (err) {
      console.error("Failed to update enrollment:", err);
      const apiError = parseApiError(err);
      setError(apiError.message);
      showError(apiError.message, "Update Failed");
    } finally {
      setLoading(false);
    }
  };

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "completed", label: "Completed" },
    { value: "cancelled", label: "Cancelled" },
    { value: "expired", label: "Expired" },
    { value: "pending", label: "Pending" },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Enrollment">
      <div className="space-y-4">
        {error && (
          <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg border border-red-100">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Student
          </label>
          <div className="text-sm text-gray-900 font-medium">
            {enrollment?.user?.firstName} {enrollment?.user?.lastName} (
            {enrollment?.user?.email})
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Course
          </label>
          <div className="text-sm text-gray-900 font-medium">
            {enrollment?.course?.title}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              options={statusOptions}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expiry Date
            </label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
              value={enrollmentExpiresAt}
              onChange={(e) => setEnrollmentExpiresAt(e.target.value)}
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={loading}>
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
